import { z } from "zod";
import {
  COUNTRY_CODES,
  CURRENCIES,
  LANGS,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  TIMEZONE_IDS,
} from "./constants";
import { isValidHours } from "./hours";

// Request contracts shared by the API (authoritative validation) and the web forms (early feedback).
// Messages follow the Meniova tone of voice: short, second person singular.

const email = z
  .string()
  .trim()
  .toLowerCase()
  .max(254, "Adresa de e-mail este prea lungă.")
  .pipe(z.email("Adaugă o adresă de e-mail validă."));

const password = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Parola trebuie să aibă cel puțin ${PASSWORD_MIN_LENGTH} caractere.`)
  .max(PASSWORD_MAX_LENGTH, "Parola este prea lungă.");

const token = z.string().regex(/^[A-Za-z0-9_-]{43}$/, "Linkul nu este valid.");

export const registerSchema = z.object({
  name: z.string().trim().min(1, "Adaugă numele tău.").max(100, "Numele este prea lung."),
  email,
  password,
  acceptTerms: z.literal(true, "Trebuie să accepți termenii pentru a crea contul."),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email,
  password: z.string().min(1, "Introdu parola.").max(PASSWORD_MAX_LENGTH),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const emailOnlySchema = z.object({ email });

/** The second sign-in step of a super admin: the challenge from /login and a 6-digit or recovery code. */
export const mfaCodeSchema = z.object({
  challenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/, "Verificarea a expirat."),
  code: z.string().trim().min(6, "Introdu codul.").max(20, "Codul nu este corect."),
});
export const mfaSetupCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "Introdu cele 6 cifre din aplicație."),
});

export const tokenSchema = z.object({ token });

export const passwordResetConfirmSchema = z.object({ token, password });

export const passwordChangeSchema = z
  .object({
    currentPassword: z.string().min(1, "Introdu parola actuală.").max(PASSWORD_MAX_LENGTH),
    newPassword: password,
  })
  .refine((v) => v.currentPassword !== v.newPassword, {
    path: ["newPassword"],
    message: "Parola nouă trebuie să fie diferită de cea actuală.",
  });

export const createRestaurantSchema = z.object({
  name: z.string().trim().min(1, "Adaugă numele restaurantului.").max(80, "Numele este prea lung."),
  city: z.string().trim().max(80, "Numele orașului este prea lung.").optional().default(""),
  country: z.enum(COUNTRY_CODES, "Alege țara."),
  defaultLang: z.enum(LANGS, "Alege limba meniului."),
  currency: z.enum(CURRENCIES, "Alege moneda."),
  timezone: z.enum(TIMEZONE_IDS, "Alege fusul orar."),
  /** Cloudflare Turnstile token, required only when the challenge is configured (lib/turnstile.ts). */
  challenge: z.string().max(4096).optional(),
});
export type CreateRestaurantInput = z.infer<typeof createRestaurantSchema>;

/** A restaurant prepared by Meniova for a client (owner 2026-11-04): the plan and the free days are preset. */
export const preparedRestaurantSchema = z.object({
  name: z.string().trim().min(1, "Adaugă numele restaurantului.").max(80, "Numele este prea lung."),
  city: z.string().trim().max(80, "Numele orașului este prea lung.").optional().default(""),
  planId: z.enum(["standard_month", "standard_year"], "Alege planul."),
  trialDays: z.coerce
    .number()
    .int("Scrie un număr întreg de zile.")
    .min(1, "Cel puțin o zi gratuită (plata de azi e 0 €).")
    .max(90, "Cel mult 90 de zile gratuite."),
});
export type PreparedRestaurantInput = z.infer<typeof preparedRestaurantSchema>;

/** Taking over a prepared restaurant through its link: a new account (or the signed-in one, with no fields). */
export const claimSchema = z.union([
  z.object({ useSession: z.literal(true) }),
  z.object({
    name: z.string().trim().min(1, "Adaugă numele tău.").max(100, "Numele este prea lung."),
    email,
    password,
    acceptTerms: z.literal(true, "Trebuie să accepți termenii pentru a crea contul."),
  }),
]);
export type ClaimInput = z.infer<typeof claimSchema>;

/** A support chat message (owner 2026-11-03). */
export const chatMessageSchema = z.object({
  body: z.string().trim().min(1, "Scrie mesajul.").max(4000, "Mesajul este prea lung."),
});

/** Super admin back-office actions (owner 2026-11-03, "Admin nou"). */
export const adminTrialSchema = z.object({
  days: z.number().int().min(1, "Cel puțin o zi.").max(90, "Cel mult 90 de zile."),
});
export const adminNoteSchema = z.object({
  body: z.string().trim().min(1, "Scrie nota.").max(2000, "Nota este prea lungă."),
});
/** Actions that let a restaurant spend money again (AI counter reset) need an explicit confirmation. */
export const adminConfirmSchema = z.object({ confirm: z.literal(true, "Confirmă acțiunea.") });

/** Super admin invitation: the account is created by the team, the invitee sets the password. */
export const adminInviteSchema = z.object({
  email,
  name: z.string().trim().min(1, "Adaugă numele.").max(100, "Numele este prea lung."),
  superadmin: z.boolean().default(false),
});
export type AdminInviteInput = z.infer<typeof adminInviteSchema>;

/** A further location of a restaurant (owner 2026-11-04): its own name, address, phone and hours. */
const locText = (max: number) => z.string().trim().max(max, "Textul este prea lung.").default("");
export const locationSchema = z.object({
  name: z.string().trim().min(1, "Scrie numele locației.").max(60, "Numele este prea lung."),
  address: locText(120),
  city: locText(120),
  phone: locText(40).refine(
    (v) => !v || /^\+?[\d\s().-]{7,20}$/.test(v),
    "Folosește cifre, spații și +, de ex. +40 269 000 000.",
  ),
  hours: locText(400).refine(
    isValidHours,
    "Verifică programul: orele au forma 12:00, iar ora de închidere e diferită.",
  ),
});
export type LocationInput = z.infer<typeof locationSchema>;
/** Adding one: the amount charged today that the owner saw and confirmed (null = nothing charged today). */
export const addLocationSchema = locationSchema.extend({
  expectedChargeMinor: z.number().int().min(0).nullable(),
});
